import {
  Box,
  FlexBox,
  Tag,
  Text,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import {
  ArticleListItemResultFragment,
  PublicationStatus,
  TranslationTitle,
} from 'lib-knowledge-management-common/graphql';
import { extractTitleFromTranslations } from 'lib-knowledge-management-common/utils/translations';
import { countDisplay } from 'lib-knowledge-utils/string';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { NodeLockIconWithTooltip } from 'components/icons/nodeLockIcon/nodeLockIcon';
import { NonDefaultLanguageBadge } from 'components/nonDefaultLanguageBadge';

import { Article } from '../types';
import {
  buildArticleEditorUrl,
  getPublicationStatusLabelAndColor,
} from '../utils';

import styles from './cellStyles.module.scss';
import { groupTitlesByCollection } from './utils';

function CategoryLabel({
  parentCategories,
}: {
  parentCategories: Article['parentCategories'];
}): JSX.Element {
  const { collections, totalCategories } = useMemo(
    () =>
      groupTitlesByCollection({
        parentCategories,
      }),
    [parentCategories],
  );

  if (parentCategories.length === 0) return <>No category • </>;

  const tooltipContent = (
    <ul className={styles.outerListWrapper}>
      {collections.map((collection) => {
        const categoryTitles = collection.categories;
        const collectionTitle = collection.title;
        return (
          <li key={collection.id} className={styles.listItem}>
            {collectionTitle}
            <ul className={styles.innerListWrapper}>
              {categoryTitles.map((categoryTitle) => (
                <li key={categoryTitle}>{categoryTitle}</li>
              ))}
            </ul>
          </li>
        );
      })}
    </ul>
  );

  return (
    <Tooltip content={tooltipContent}>
      {totalCategories === 1
        ? `${collections[0].categories[0]} • `
        : `${totalCategories} categories • `}
    </Tooltip>
  );
}

/** Table Cells */
// Title and Article Link
export function ArticleHeader(): JSX.Element {
  return <span>Title</span>;
}
type ArticleUser = ArticleListItemResultFragment['createdBy'];
type ArticleCellProps = {
  createdAt: number;
  createdBy?: ArticleUser;
  updatedAt: number;
  updatedBy?: ArticleUser;
  titles: TranslationTitle[];
  articleId: string;
  parentCategories: Article['parentCategories'];
};
export function ArticleCell({
  createdAt,
  createdBy,
  updatedAt,
  updatedBy,
  titles,
  articleId,
  parentCategories,
}: ArticleCellProps): JSX.Element {
  const updatedDate = new Date(updatedAt);
  const updatedTooltip = `${updatedDate.toLocaleDateString()}, ${updatedDate.toLocaleTimeString()} by ${
    updatedBy?.name || 'unknown'
  }`;

  const createdDate = new Date(createdAt);
  const createdTooltip = `${createdDate.toLocaleDateString()}, ${createdDate.toLocaleTimeString()} by ${
    createdBy?.name || 'unknown'
  }`;
  const title = extractTitleFromTranslations(titles);

  return (
    <Link to={buildArticleEditorUrl({ articleId })}>
      <Text.Heading variant="heading-3">
        {title}
        <NonDefaultLanguageBadge titles={titles} />
      </Text.Heading>
      <Box>
        <Text.Paragraph variant="3" subdued>
          <CategoryLabel parentCategories={parentCategories} />
          Edited{' '}
          <Tooltip content={updatedTooltip}>
            <Timestamp
              time={updatedAt / 1000}
              refreshRate={60000}
              relative={true}
            />
          </Tooltip>{' '}
          • Created{' '}
          <Tooltip content={createdTooltip}>
            <Timestamp
              time={createdAt / 1000}
              refreshRate={60000}
              relative={true}
            />
          </Tooltip>
        </Text.Paragraph>
      </Box>
    </Link>
  );
}

// Collections
export function CollectionHeader(): JSX.Element {
  return <span>Collections</span>;
}
type CollectionCellProps = {
  parentCollections: Article['parentCollections'];
  isSyncedWithElevio: boolean;
};
export function CollectionCell({
  parentCollections,
  isSyncedWithElevio,
}: CollectionCellProps): JSX.Element {
  const collections = parentCollections
    .map((collection) => collection.title)
    .join(', ');

  if (!collections) return <>No Collection</>;

  return (
    <FlexBox flexDirection="column">
      <FlexBox justifyContent="flex-start" alignItems="center" gap="100">
        <Text.Heading variant="heading-3">{collections}</Text.Heading>
        {isSyncedWithElevio && (
          <NodeLockIconWithTooltip label="Synced from Elevio" />
        )}
      </FlexBox>
      <Text.Paragraph variant="3" subdued>
        {countDisplay({
          count: parentCollections.reduce(
            (nQueues, collection) =>
              collection.linkedResources.length + nQueues,
            0,
          ),
          singular: 'queue',
          plural: 'queues',
        })}
      </Text.Paragraph>
    </FlexBox>
  );
}

// Status
export function PublicationStatusHeader(): JSX.Element {
  return <span>Status</span>;
}
type PublicationStatusCellProps = {
  publicationStatus: PublicationStatus | null | undefined;
};
export function PublicationStatusCell({
  publicationStatus,
}: PublicationStatusCellProps): JSX.Element {
  if (!publicationStatus) {
    return <></>;
  }
  const { label, color } = getPublicationStatusLabelAndColor(publicationStatus);
  return <Tag color={color}>{label}</Tag>;
}
